import React from 'react';

import {
  FlowContextProvider,
  FlowValues,
  StepWrapper,
  toCentsFromString,
  toMoney,
  useMutation,
  useQuery,
} from '@betterment/js-runtime';
import { Helmet } from 'react-helmet';
import { Route, Routes, useMatch, useSearchParams } from 'react-router-dom';

import WithdrawalFlowAmount from './Amount';
import WithdrawalFlowConfirm from './Confirm';

import { useAccount } from '../Checking/components/AccountProvider';
import { useCustomer } from '../Checking/components/CustomerProvider';
import { ExternalAccount, TransferAvailabilityEstimate, WithdrawalParams } from '../Checking/data';
import { CloseButton, ModalContent, ModalOverlay } from '../Checking/modal-helpers';
import { externalAccountCreationPath } from '../Checking/path-helpers';
import FullPageLoader from '../Shared/components/FullPageLoader';
import SuccessStep from '../Shared/components/SuccessStep';
import { locationAssign } from '../Shared/path-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

export const withdrawalValues: { externalAccountId: number | null; amount: string } = {
  externalAccountId: null,
  amount: '',
};

export type WithdrawalValues = FlowValues<{
  amount: string;
  externalAccountId: ExternalAccount['id'];
}>;

const WithdrawalFlowController: React.FC = () => {
  const { customer } = useCustomer();
  const { account } = useAccount();

  const accountId = account.id;
  const [postWithdrawal] = useMutation<{}, WithdrawalParams>(urlBuilder.postWithdrawal(accountId));
  const { data: availabilityEstimate, loading: isLoadingEstimate } = useQuery<TransferAvailabilityEstimate>(
    urlBuilder.getWithdrawalAvailabilityEstimate()
  );

  const handleClose = () => locationAssign(urlBuilder.getAppCheckingSummary.absolute({ accountId }));
  const [searchParams] = useSearchParams();
  const amount = searchParams.get('amount') || '';
  const onWithdrawal = (values: WithdrawalValues) => {
    return postWithdrawal({
      amountCents: toMoney(toCentsFromString(values.amount)).cents,
      externalAccountId: values.externalAccountId,
    });
  };

  const hasExternalAccount = !!customer?.externalAccount;

  const match = useMatch('/checking/accounts/:accountId/withdrawal_setup/*');

  React.useEffect(() => {
    if (!hasExternalAccount) locationAssign(externalAccountCreationPath(accountId, 'withdrawal'));
  }, [accountId, hasExternalAccount]);

  if (isLoadingEstimate || customer.externalAccount == null) {
    return <FullPageLoader />;
  }

  return (
    <ModalOverlay>
      <Helmet>
        <title>Withdraw</title>
      </Helmet>
      <ModalContent aria-label="checking withdrawal flow">
        <CloseButton closeHandler={handleClose} />
        <FlowContextProvider<WithdrawalValues>
          initialValues={{
            ...withdrawalValues,
            externalAccountId: customer.externalAccount?.id,
            amount,
          }}
          returnToPath={urlBuilder.getAppCheckingSummary.absolute({ accountId })}
          basePath={match!.pathnameBase}
          routePath=""
        >
          <Routes>
            <Route
              path="/amount"
              element={
                <StepWrapper<WithdrawalValues> name="amount">
                  {stepProps => <WithdrawalFlowAmount {...stepProps} customer={customer} />}
                </StepWrapper>
              }
            />
            <Route
              path="/confirm"
              element={
                <StepWrapper<WithdrawalValues> name="confirm">
                  {stepProps => (
                    <WithdrawalFlowConfirm
                      {...stepProps}
                      customer={customer}
                      availabilityEstimate={availabilityEstimate!}
                      onWithdrawal={onWithdrawal}
                    />
                  )}
                </StepWrapper>
              }
            />
            <Route
              path="/success"
              element={
                <StepWrapper name="success">
                  <SuccessStep
                    message="Your withdrawal has been submitted"
                    eventProperties={{ name: 'WithdrawalSuccess', flow: 'WithdrawalFromChecking' }}
                  />
                </StepWrapper>
              }
            />
          </Routes>
        </FlowContextProvider>
      </ModalContent>
    </ModalOverlay>
  );
};

export default WithdrawalFlowController;
